import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  REPORT_WEEK_STARTS,
  REPORTING_ATTENTION_DAYS_MAX,
  REPORTING_ATTENTION_DAYS_MIN,
  type ReportWeekStart,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { apiErrorMessage } from '@/services/apiClient';
import { useReportingSettingsQuery, useUpdateReportingSettings } from '../api/reports.api';

interface Draft {
  weekStartsOn: ReportWeekStart;
  readyForCollectionAttentionDays: string;
  labStageAttentionDays: string;
  orderedNotSentAttentionDays: string;
}

const DAY_FIELDS = [
  'readyForCollectionAttentionDays',
  'labStageAttentionDays',
  'orderedNotSentAttentionDays',
] as const;

/**
 * Settings › Reporting. Tenant policy: the week start and the optical attention thresholds.
 * Smart Reports are shown for what they are in this release — off, and not yet available.
 */
export function ReportingSettingsPage() {
  const { t } = useTranslation();
  const settings = useReportingSettingsQuery();
  const update = useUpdateReportingSettings();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings.data) return;
    setDraft({
      weekStartsOn: settings.data.weekStartsOn,
      readyForCollectionAttentionDays: String(settings.data.readyForCollectionAttentionDays),
      labStageAttentionDays: String(settings.data.labStageAttentionDays),
      orderedNotSentAttentionDays: String(settings.data.orderedNotSentAttentionDays),
    });
  }, [settings.data]);

  if (settings.isPending || !draft) return <LoadingState />;
  if (settings.isError) {
    return <Alert tone="error">{apiErrorMessage(settings.error) ?? t('reports.loadError')}</Alert>;
  }

  const invalid = DAY_FIELDS.filter((field) => {
    const value = Number(draft[field]);
    return (
      !Number.isInteger(value) ||
      value < REPORTING_ATTENTION_DAYS_MIN ||
      value > REPORTING_ATTENTION_DAYS_MAX
    );
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (invalid.length > 0 || !settings.data) return;
    setSaved(false);
    update.mutate(
      {
        version: settings.data.version,
        weekStartsOn: draft.weekStartsOn,
        readyForCollectionAttentionDays: Number(draft.readyForCollectionAttentionDays),
        labStageAttentionDays: Number(draft.labStageAttentionDays),
        orderedNotSentAttentionDays: Number(draft.orderedNotSentAttentionDays),
      },
      {
        onSuccess: () => setSaved(true),
        onError: () => void settings.refetch(),
      },
    );
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.settings.title')} subtitle={t('reports.settings.subtitle')} />
      <form onSubmit={submit} className="space-y-4">
        <Card>
          <CardHeader title={t('reports.settings.calendar')} />
          <div className="px-5 pb-5">
            <label className="flex max-w-xs flex-col gap-1">
              <span className="text-muted text-xs font-medium">
                {t('reports.settings.weekStartsOn')}
              </span>
              <Select
                id="report-week-start"
                value={draft.weekStartsOn}
                options={REPORT_WEEK_STARTS.map((day) => ({
                  value: day,
                  label: t(`reports.weekdays.${day}`),
                }))}
                onChange={(event) =>
                  setDraft({ ...draft, weekStartsOn: event.target.value as ReportWeekStart })
                }
              />
            </label>
          </div>
        </Card>
        <Card>
          <CardHeader
            title={t('reports.settings.attention')}
            subtitle={t('reports.settings.attentionHint')}
          />
          <div className="grid gap-4 px-5 pb-5 sm:grid-cols-3">
            {DAY_FIELDS.map((field) => (
              <Input
                key={field}
                id={`report-${field}`}
                label={t(`reports.settings.${field}`)}
                type="number"
                inputMode="numeric"
                min={REPORTING_ATTENTION_DAYS_MIN}
                max={REPORTING_ATTENTION_DAYS_MAX}
                value={draft[field]}
                error={invalid.includes(field) ? t('reports.settings.rangeHint') : undefined}
                onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
              />
            ))}
          </div>
        </Card>
        <Card>
          <CardHeader title={t('reports.smart.pageTitle')} />
          <p className="text-muted px-5 pb-5 text-sm">{t('reports.settings.smartOff')}</p>
        </Card>
        {update.isError ? (
          <Alert tone="error">{apiErrorMessage(update.error) ?? t('reports.loadError')}</Alert>
        ) : null}
        {saved ? <Alert tone="success">{t('reports.settings.saved')}</Alert> : null}
        <Button type="submit" disabled={invalid.length > 0 || update.isPending}>
          {t('reports.settings.save')}
        </Button>
      </form>
    </div>
  );
}
